import React, {useRef} from 'react';
import {Modal} from 'react-bootstrap';
import {useQuery} from '@tanstack/react-query';
import {UpdateFormPage} from '../pages/UpdateFormPage';
import {apiService} from '../services/api';
import {Screen} from '../types';
import {useFilters} from '../contexts';
import {screenRequestFilters} from '../utils/contextFilters';

interface UpdateFormModalProps {
    show: boolean;
    onHide: () => void;
    form: any;
    /** The row as the table knows it; the record itself is fetched fresh, shaped by the form. */
    row: any;
    appId: string;
    allScreens: Screen[];
    onSuccess?: (data?: any) => void;
}

/** A record's update form inside a modal - e.g. editing a lookup row from the many-to-one picker. */
export const UpdateFormModal: React.FC<UpdateFormModalProps> = ({show, onHide, form, row, appId, allScreens, onSuccess}) => {
    const isDynamicForm = form?.kind === 'dynamic';
    const rowId = row?.id;
    const {getFilter} = useFilters();
    // The record is a row of the form's screen, so it is asked for with that screen's list parameters.
    const recordFilters = screenRequestFilters(allScreens.find(screen => screen.id === form?.screenId), getFilter);
    // The record as read while the modal is open. Once it was read, the list no longer returning it
    // is no missing record: saving can take it out of the screen's filter.
    const recordKey = `${form?.screenId}:${rowId}:${JSON.stringify(recordFilters)}`;
    const lastRead = useRef<{key: string; record: any} | null>(null);
    const {data: freshRowData} = useQuery({
        queryKey: isDynamicForm
            ? ['dynamicFormData', form?.id, String(rowId), 'detail']
            : ['screenData', form?.screenId, String(rowId), 'detail', JSON.stringify(recordFilters)],
        queryFn: async () => {
            if (isDynamicForm) {
                const res = await apiService.getDynamicFormRecord(form.id, rowId);
                if (!res.success) return null;
                const payload: any = res.data;
                return payload?.data ?? payload;
            }
            const readBefore = lastRead.current?.key === recordKey ? lastRead.current.record : null;
            const res = await apiService.getScreenRecord(form.screenId, rowId, recordFilters, readBefore === null);
            if (!res.success) return readBefore;
            const payload: any = res.data;
            const record = payload?.data ?? payload;
            lastRead.current = {key: recordKey, record};
            return record;
        },
        enabled: show && rowId !== undefined && (isDynamicForm ? !!form?.id : !!form?.screenId),
        refetchOnMount: 'always',
    });

    if (!form || rowId === undefined) return null;
    const rowData = freshRowData || row;

    return (
        <Modal show={show} onHide={onHide} size="xl" className="modal-blur" backdrop="static" data-e2e={`updatemodal:${form.id}:${rowId}`}>
            <Modal.Header closeButton>
                <Modal.Title>{form.displayName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <UpdateFormPage
                    form={form}
                    rowData={rowData}
                    appId={appId}
                    allScreens={allScreens}
                    embedded={true}
                    onSuccess={(data) => {
                        onSuccess?.(data);
                        onHide();
                    }}
                />
            </Modal.Body>
        </Modal>
    );
};
